import React, { memo, useEffect, useRef, useState } from 'react'
import styles from './CommonDatePicker.module.scss'
import { Calendar } from 'react-date-range'

type Props = {
    initialDate: Date,
    onChange: (date: Date) => void
}

const CommonDatePicker: React.FC<Props> = memo((props: Props) => {
    const { initialDate, onChange } = props

    const [showOnTop, setShowOnTop] = useState(false);
    const dateRef = useRef<HTMLButtonElement>(null)

    const [expanded, setExpanded] = useState(false)
    const [selectionDate, setSelectionDate] = useState<Date>(initialDate)

    function toggleExpanded() {
        setExpanded(!expanded)
    }

    const handleSelect = (date: Date) => {
        setSelectionDate(date)
        setExpanded(false)
        onChange(date)
    }

    function closeDatePicker(event: React.FocusEvent) {
        if (event.relatedTarget) return

        setExpanded(false)
    }

    useEffect(() => {
        if (dateRef.current === null) return

        if (!expanded) return

        const dropdown = dateRef
        if (!dropdown) return

        const dropdownRect = dropdown.current!.getBoundingClientRect()
        const bottomOverflow = window.innerHeight - (dropdownRect.bottom + 400)
        const topOverflow = dropdownRect.top - 400

        if (bottomOverflow < 0) {
            setShowOnTop(true);
        } else if (topOverflow < 0) {
            setShowOnTop(false);
        } else {
            setShowOnTop(false);
        }
    }, [expanded, dateRef])

    useEffect(() => {
        setSelectionDate(initialDate)
    }, [initialDate])

    return (
        <div className={styles.container} onBlur={closeDatePicker}>
            <button ref={dateRef} className={`standardButton ${styles.input}`} onClick={toggleExpanded}>
                <div className={styles.valueContainer}>
                    {
                        `${selectionDate?.toLocaleDateString("en-gb")}`
                    }
                    <svg width="18" height="20" viewBox="0 0 18 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M2.3077 19.5001C1.80257 19.5001 1.375 19.3251 1.025 18.9751C0.675 18.6251 0.5 18.1975 0.5 17.6924V4.30784C0.5 3.80271 0.675 3.37514 1.025 3.02514C1.375 2.67514 1.80257 2.50014 2.3077 2.50014H3.69233V1.15397C3.69233 0.934749 3.76573 0.751741 3.91253 0.604941C4.05931 0.458157 4.24232 0.384766 4.46155 0.384766C4.68077 0.384766 4.86378 0.458157 5.01058 0.604941C5.15736 0.751741 5.23075 0.934749 5.23075 1.15397V2.50014H12.8077V1.13474C12.8077 0.921925 12.8794 0.743724 13.023 0.60014C13.1666 0.456557 13.3448 0.384766 13.5576 0.384766C13.7704 0.384766 13.9486 0.456557 14.0922 0.60014C14.2358 0.743724 14.3076 0.921925 14.3076 1.13474V2.50014H15.6923C16.1974 2.50014 16.625 2.67514 16.975 3.02514C17.325 3.37514 17.5 3.80271 17.5 4.30784V17.6924C17.5 18.1975 17.325 18.6251 16.975 18.9751C16.625 19.3251 16.1974 19.5001 15.6923 19.5001H2.3077ZM2.3077 18.0001H15.6923C15.7692 18.0001 15.8397 17.9681 15.9038 17.904C15.9679 17.8398 16 17.7693 16 17.6924V8.30784H1.99997V17.6924C1.99997 17.7693 2.03202 17.8398 2.09612 17.904C2.16024 17.9681 2.23077 18.0001 2.3077 18.0001ZM1.99997 6.80787H16V4.30784C16 4.23091 15.9679 4.16038 15.9038 4.09627C15.8397 4.03217 15.7692 4.00012 15.6923 4.00012H2.3077C2.23077 4.00012 2.16024 4.03217 2.09612 4.09627C2.03202 4.16038 1.99997 4.23091 1.99997 4.30784V6.80787ZM8.99998 12.077C8.75511 12.077 8.54646 11.9908 8.37403 11.8184C8.20159 11.646 8.11538 11.4373 8.11538 11.1924C8.11538 10.9476 8.20159 10.7389 8.37403 10.5665C8.54646 10.3941 8.75511 10.3078 8.99998 10.3078C9.24484 10.3078 9.45349 10.3941 9.62593 10.5665C9.79836 10.7389 9.88457 10.9476 9.88457 11.1924C9.88457 11.4373 9.79836 11.646 9.62593 11.8184C9.45349 11.9908 9.24484 12.077 8.99998 12.077ZM4.99998 12.077C4.75511 12.077 4.54646 11.9908 4.37403 11.8184C4.20159 11.646 4.11538 11.4373 4.11538 11.1924C4.11538 10.9476 4.20159 10.7389 4.37403 10.5665C4.54646 10.3941 4.75511 10.3078 4.99998 10.3078C5.24484 10.3078 5.45349 10.3941 5.62593 10.5665C5.79836 10.7389 5.88458 10.9476 5.88458 11.1924C5.88458 11.4373 5.79836 11.646 5.62593 11.8184C5.45349 11.9908 5.24484 12.077 4.99998 12.077ZM13 12.077C12.7551 12.077 12.5465 11.9908 12.374 11.8184C12.2016 11.646 12.1154 11.4373 12.1154 11.1924C12.1154 10.9476 12.2016 10.7389 12.374 10.5665C12.5465 10.3941 12.7551 10.3078 13 10.3078C13.2448 10.3078 13.4535 10.3941 13.6259 10.5665C13.7984 10.7389 13.8846 10.9476 13.8846 11.1924C13.8846 11.4373 13.7984 11.646 13.6259 11.8184C13.4535 11.9908 13.2448 12.077 13 12.077ZM8.99998 16.0001C8.75511 16.0001 8.54646 15.9139 8.37403 15.7415C8.20159 15.569 8.11538 15.3604 8.11538 15.1155C8.11538 14.8706 8.20159 14.662 8.37403 14.4896C8.54646 14.3171 8.75511 14.2309 8.99998 14.2309C9.24484 14.2309 9.45349 14.3171 9.62593 14.4896C9.79836 14.662 9.88457 14.8706 9.88457 15.1155C9.88457 15.3604 9.79836 15.569 9.62593 15.7415C9.45349 15.9139 9.24484 16.0001 8.99998 16.0001ZM4.99998 16.0001C4.75511 16.0001 4.54646 15.9139 4.37403 15.7415C4.20159 15.569 4.11538 15.3604 4.11538 15.1155C4.11538 14.8706 4.20159 14.662 4.37403 14.4896C4.54646 14.3171 4.75511 14.2309 4.99998 14.2309C5.24484 14.2309 5.45349 14.3171 5.62593 14.4896C5.79836 14.662 5.88458 14.8706 5.88458 15.1155C5.88458 15.3604 5.79836 15.569 5.62593 15.7415C5.45349 15.9139 5.24484 16.0001 4.99998 16.0001ZM13 16.0001C12.7551 16.0001 12.5465 15.9139 12.374 15.7415C12.2016 15.569 12.1154 15.3604 12.1154 15.1155C12.1154 14.8706 12.2016 14.662 12.374 14.4896C12.5465 14.3171 12.7551 14.2309 13 14.2309C13.2448 14.2309 13.4535 14.3171 13.6259 14.4896C13.7984 14.662 13.8846 14.8706 13.8846 15.1155C13.8846 15.3604 13.7984 15.569 13.6259 15.7415C13.4535 15.9139 13.2448 16.0001 13 16.0001Z" fill="#1C1B1F" />
                    </svg>
                </div>
            </button>
            <div className={`${styles.datePickerContainer} ${expanded ? styles.show : ''} ${showOnTop ? styles.selectExpandedTop : ''}`}
            >
                <Calendar
                    date={selectionDate}
                    onChange={handleSelect}
                />
            </div>
        </div>
    )
})

export default CommonDatePicker